'use client';

import { useCallback, useId, useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import type { CoverageTimelineView } from '@/types/coverage-timeline';
import {
  TimelineChart, TimelineLegend, timelineDomain, drawable,
} from '../missions/_components/MissionCoverageTimeline';
import { utcClock, utcStamp, formatDuration, fromRow } from '../missions/_components/coverageFormatters';
import { launchUnderWay } from '../missions/_components/coveragePresentation';
import { CoverageActionCard } from '../missions/_components/CoverageActionCard';
import type { RotationForecastView } from '../missions/_components/coverageTypes';
import type { RotationPhasePayload } from '@/hooks/useWebSocket';
import { CopCoverageLifecycle } from './CopCoverageLifecycle';
import { useCopRotationControls } from './useCopRotationControls';
import {
  ribbonSegments, ribbonCoveredFraction, nextGap, hasUnknown, type RibbonSegment,
} from './copCoverageRibbon';
import type { CoverageSummary } from './useCopCoverage';

/**
 * THE ROTATION, ON THE COP.
 *
 * Everything continuous coverage can tell an operator has lived on `/missions/[id]` -
 * `MissionCoveragePanel` is 2,860 lines of it - and the COP has carried one chip per row. That is
 * a page navigation away from the live air picture at the moment an aircraft is about to leave a
 * pad, which is the context switch the whole COP-missions convergence exists to remove. Field
 * evidence for doing it now: #505 note 7, "when a second aircraft is present, we should
 * immediately see its camera and how it replaces the first."
 *
 * WHAT SURVIVED THE TRIP TO 320 px, and that is the entire design question. Not the panel: four
 * readings and a ribbon. The ribbon answers "is the station manned, across the window"; the four
 * readings answer "and what happens next". The per-aircraft Gantt is one press away, drawn over
 * the map where there is room for it, because a five-lane chart in this column is a smear.
 *
 * THIS COMPONENT COMMANDS NOTHING. No Approve, no Rotate now, no Detach - those are PR 3, and they
 * arrive with the refusal sentences and the arm-then-confirm dwell that `CoverageActionCard`
 * already carries. Shipping the reading first means the surface an operator learns to trust exists
 * before anything on it can launch an aircraft.
 */

interface CopCoverageBlockProps {
  /** The fleet-wide row for this mission - state, target, window. Never null when this renders. */
  summary: CoverageSummary;
  /** The forecast, from `useCopRotation`. Null while the first read is in flight. */
  rotation: RotationForecastView | null;
  timeline: CoverageTimelineView | null;
  /**
   * A rotation read has failed and none has succeeded since.
   *
   * Drawn, never swallowed. An empty forecast and an unread one are the same pixels, and only one
   * of them is a statement about the fleet - `useCopCoverage`'s own comment makes the argument for
   * the chip and it holds harder here, because this is the reading PR 3's controls gate on.
   */
  degraded: boolean;
  /** When the last successful read landed, so a stale block can say how stale. */
  lastReadAt: number | null;
  /**
   * Read it again now.
   *
   * The degraded note is the one place on this block that reports a failure, and without this it
   * reported it and then offered nothing - the operator waited out a 15 s poll. Optional so the
   * component stays renderable in isolation.
   */
  onRetry?: () => void;
  /**
   * May this operator command the rotation? `role === 'admin' || role === 'operator'`, which is
   * the same gate all five endpoints carry.
   *
   * A VIEWER SEES NO CONTROLS AT ALL rather than greyed-out ones. That is this COP's existing rule,
   * stated where the zones tab applies it: "The tab is not disabled for them, it is absent: an
   * admin control that greys out on a viewer's screen teaches them nothing, and this is a
   * permission rather than a state that can change while they watch." Every READING here stays for
   * a viewer - the whole block is useful without a single button.
   */
  canCommand: boolean;
  /** Re-read the rotation after a control lands, and behind the degraded note's Retry. */
  onChanged: () => void;
}

/**
 * The three coverage states the row chip above deliberately draws nothing for, and what this block
 * has to say about each.
 *
 * `stopped` is terminal: editing the window does not revive it, and a force-detach plus re-attach
 * is the only way back. `suspended` is a person having paused the rotation. `pending_station` is
 * coverage attached with nobody there yet. In all three the forecast below is not a plan anyone is
 * flying, and the ribbon under it is mostly history.
 */
const QUIET_STATE: Readonly<Partial<Record<string, { label: string; note: string }>>> = {
  pending_station: {
    label: 'Pending',
    note: 'Coverage is attached but no aircraft has taken the station yet.',
  },
  suspended: {
    label: 'Suspended',
    note: 'The rotation is suspended - no replacement will be launched until it is resumed.',
  },
  stopped: {
    label: 'Stopped',
    note: 'The rotation is stopped. This is terminal - it cannot be restarted by editing the window.',
  },
};

/** Smallest a gap may be drawn, in CSS px, so a short hole cannot round away to nothing. */
const MIN_GAP_PX = 3;

/**
 * How a segment is painted.
 *
 * SOLID MARKER TOKENS, NOT ALPHA STEPS. The first version used `bg-status-green/45` against
 * `/75` and `bg-status-red/35`, which measures 1.65:1 between a forecast hole and forecast
 * coverage - the exact boundary an operator scans for, and less than half of SC 1.4.11's 3:1. In
 * the light theme it fell to about 1.15:1, where the gap is simply not drawn. The `-marker` tokens
 * exist for this and are defined per theme.
 *
 * The future is a HATCH rather than a lighter shade, so past-versus-future survives greyscale, a
 * deuteranopic viewer, and a sunlit field screen - the sibling chart's own rule is that a
 * distinction must never ride on one channel alone.
 */
function segmentPaint(s: RibbonSegment): { background: string } {
  const color = s.kind === 'covered'
    ? 'var(--status-green-marker)'
    : s.kind === 'gap' ? 'var(--status-red-marker)' : 'var(--muted-foreground)';
  if (s.past) return { background: color };
  return {
    background: `repeating-linear-gradient(135deg, ${color} 0 4px, color-mix(in srgb, ${color} 45%, transparent) 4px 8px)`,
  };
}

export function CopCoverageBlock({
  summary, rotation, timeline: rawTimeline, degraded, lastReadAt, onRetry,
  canCommand, onChanged,
}: CopCoverageBlockProps) {
  const timezone = useOrgTimezone();
  const [expanded, setExpanded] = useState(false);
  /**
   * THE ONE ARMED CONTROL, AND WHAT IT IS ARMED AGAINST.
   *
   * Two defects, both of which `MissionCoveragePanel` found and fixed once already, and both of
   * which the first cut of this block re-introduced:
   *
   *   1. THE ARM WAS A BARE TIMESTAMP. Nothing cleared it when the proposal it was armed against
   *      went away. Press ROTATE NOW, change your mind, press CANCEL - the card's own copy says a
   *      new unapproved proposal is scheduled straight away, the poll brings it back, the button
   *      still reads "Confirm rotate now" and the 600 ms dwell expired minutes ago. ONE click then
   *      launches against an action the operator never armed. The planner superseding the row in
   *      place reaches the same place without any second press at all. Carrying the `actionId`
   *      makes `rotateArmed` DERIVED, so an arm cannot outlive its proposal.
   *   2. TWO DESTRUCTIVE CONFIRMS COULD BE LIVE AT ONCE. Rotate's arm lived here and detach's lived
   *      inside the lifecycle strip, with nothing coordinating them - so ROTATE NOW and DETACH
   *      could both sit armed about 60 px apart in a 288 px column, with flatly contradictory
   *      consequences on screen. One slot, so arming either disarms the other by construction.
   */
  const [armed, setArmed] = useState<
    { control: 'rotate'; actionId: string; at: number } | { control: 'detach'; at: number } | null
  >(null);
  const disarm = useCallback(() => setArmed(null), []);
  const headingId = useId();
  const uid = useId();
  /**
   * `disarm` IS PASSED AS `onSettled`, and that is a fix rather than a tidy-up.
   *
   * Nothing used to clear the arm when a write came back. A detach refused with 409 left the button
   * reading "Confirm detach" - character for character what it read before the press - so the
   * control looked untouched and was pressed again: two DELETEs three seconds apart in the dev log,
   * from an operator who had been told nothing. An armed destructive control must not survive the
   * request it armed, whichever way that request ended.
   *
   * NOT `onChanged`, which is also the 15 s poll and the WebSocket coalescer - hanging the disarm
   * off that would clear an arm the operator had just made, on a frame they did not cause.
   */
  const controls = useCopRotationControls(summary.mission_id, onChanged, disarm);

  /**
   * THE SAME TOLERANCE THE MISSION PAGE HAS. `drawable` exists because a payload with a missing
   * `lanes` array or an unparseable `now` throws in RENDER, and its own comment names the stakes:
   * "There is no error boundary in this app, so either one takes down the entire page." On the
   * mission page that costs one mission. Here it would be the live air picture.
   */
  const timeline = drawable(rawTimeline) ? rawTimeline : null;

  const domain = useMemo(() => timelineDomain(timeline), [timeline]);
  const segments = useMemo(() => ribbonSegments(timeline, domain), [timeline, domain]);
  const coveredFraction = useMemo(() => ribbonCoveredFraction(segments), [segments]);
  const unknownAhead = useMemo(() => hasUnknown(segments), [segments]);

  /**
   * NOW COMES FROM THE SERVER'S CHART, never from `Date.now()`.
   *
   * Two reasons, and the second is the one that bites. A `Date.now()` in a render body makes the
   * render non-idempotent - React may call it twice and get two answers. And the chart's own NOW
   * line is drawn from `view.now`, so a gap measured against the browser's clock would be marked
   * against a different instant than the one the operator can see, on a box whose clock has drifted
   * before. Null when there is no chart, which is also when there are no segments to search.
   */
  const nowMs = timeline ? new Date(timeline.now).getTime() : null;
  const gap = useMemo(
    () => (nowMs === null ? null : nextGap(segments, nowMs)),
    [segments, nowMs],
  );
  /** Where the NOW rule sits, as a percentage of the drawn window. Null when it is off the edge. */
  const nowPct = domain !== null && nowMs !== null && nowMs > domain.start && nowMs < domain.end
    ? ((nowMs - domain.start) / (domain.end - domain.start)) * 100
    : null;

  /**
   * The replacement the forecast is currently about, normalised by the SAME function the mission
   * panel uses.
   *
   * `fromRow` and not a reader of my own: `launch_at`, the relief and the margin all live inside
   * `RotationActionRow.inputs`, not as columns, and a second unpacking of that bag is exactly the
   * duplicate normalisation this feature already paid for once (W3 read the relief from the wrong
   * level and the socket carried none for a release). One function, two surfaces, one answer.
   *
   * Read from `current` and nothing else. `history` is what already happened, and a surface that
   * fell back to it when `current` is null would answer "what is coming" with "what is done" -
   * which reads identically and is the opposite claim.
   */
  const replacement = useMemo(() => fromRow(rotation?.current ?? null), [rotation]);
  const launchAt = replacement?.launchAt ?? null;
  const reliefCallsign = replacement?.relief?.callsign ?? null;
  const reliefUnavailable = replacement?.relief?.unavailableReason ?? null;
  const commitState = replacement?.commitState ?? null;

  /**
   * WHERE THE LAUNCH HAS GOT TO, from the polled row.
   *
   * The mission panel prefers a socket frame when it is fresher; this block cannot, because
   * `useCopRotation` treats a frame as a trigger to re-read rather than a payload to apply (D5).
   * The row carries `phase` and `phase_reason` as columns since 032, so both surfaces answer from
   * the same two fields either way - the COP is simply at most one round trip behind, which is the
   * price of not maintaining a second normalisation of the frame.
   */
  const phase: RotationPhasePayload | null = useMemo(() => {
    const current = rotation?.current ?? null;
    if (!current) return null;
    return {
      action_id: current.id,
      phase: current.phase ?? 'scheduled',
      phase_reason: current.phase_reason ?? null,
      relief_drone_id: current.drone_id ?? null,
      outgoing_drone_id: null,
    };
  }, [rotation]);

  /**
   * Is there anything to approve, and anything to bring forward?
   *
   * BOTH HALVES OF EACH, mirrored from the mission panel because they are different facts.
   * `rotation.current` is the row the request is about - without it there is no `action_id` to
   * send. `relief.droneId` is whether that row names an aircraft: §13 publishes a launch time for
   * a rotation nobody can fly, and approving that arms nothing at all. `rotate_now` drops the
   * launch TIME (it is replacing it) and adds the one thing Approve does not care about: a launch
   * already under way cannot be brought forward, and the server refuses it - mirrored so the
   * button is visibly unavailable rather than pressable-then-refused, which on a control that
   * commands an aircraft is the difference between a considered act and a surprise.
   */
  const reliefDroneId = replacement?.relief?.droneId ?? null;
  const canApprove = rotation?.current != null && reliefDroneId !== null && launchAt !== null;
  const canRotateNow = rotation?.current != null && reliefDroneId !== null && !launchUnderWay(phase);
  const approval = rotation?.approval ?? null;

  /**
   * A DEGRADED READ DISABLES THE CONTROLS; IT DOES NOT DELETE THEM.
   *
   * The first cut dropped the whole card on `degraded`, so a replacement waiting to be approved
   * simply vanished on one failed poll, under a note that mentioned the read and said nothing
   * about the controls. At 02:00 that reads as "the decision resolved itself" - which is a worse
   * misreading than the one the flag exists to prevent.
   *
   * `CoverageActionCard` already has the correct behaviour and the correct words for this, and the
   * first cut hardcoded the prop that turns them on to `false`. With `rotationFailed` fed the real
   * flag, the card stays on screen and says, in the sentence already written for it: "Approve is
   * unavailable - the replacement schedule could not be read."
   *
   * SUSPENDED AND STOPPED ARE NOT DECISIONS EITHER. `suspendRotation` closes the standing forecast
   * best-effort and logs-and-continues on failure, so `rotation.current` can survive a suspend -
   * and the card would then offer a live APPROVE directly beneath this block's own note saying no
   * replacement will be launched. The server refuses it, which makes it exactly the
   * pressable-then-refused control this feature's own comments forbid.
   */
  const rotationRunning = summary.state !== 'suspended' && summary.state !== 'stopped';
  const commandable = canCommand;
  const decisionOffered = commandable && rotationRunning;

  const rotateArmed = armed?.control === 'rotate'
    && armed.actionId === (rotation?.current?.id ?? null);
  const detachArmed = armed?.control === 'detach';

  return (
    <section
      aria-labelledby={headingId}
      className="mt-2 border-t divider pt-2"
      data-testid="cop-coverage-block"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <h4
          id={headingId}
          className="text-[9.5px] uppercase tracking-[0.12em] text-muted-foreground font-semibold"
        >
          Coverage
        </h4>
        {/*
          THE THREE STATES THE ROW CHIP DELIBERATELY DOES NOT DRAW.
          `CopMissionsPanel` renders a chip for covered / gap / handover only, on the grounds that
          the queue asks "is a station that should be manned manned". That is right for a row and
          wrong under it: a STOPPED rotation is terminal - editing the window does not revive it -
          and without this an operator got a full ribbon, a percentage and a swap time under a row
          with no chip at all, and nothing anywhere saying the rotation was dead.
        */}
        {QUIET_STATE[summary.state] && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-px rounded-sm border border-status-amber-marker text-status-amber-fg">
            {QUIET_STATE[summary.state]!.label}
          </span>
        )}
        {/*
          ALWAYS ENABLED, and `h-6` not `h-5`. It was `disabled` with its reason in a `title`, which
          is the pattern this feature has a standing rule against: a disabled button takes no focus
          and a `title` never fires on touch, so on a field tablet the one explanation for a dead
          control was unreachable by mouse, keyboard and screen reader alike. The dialog says
          "nothing to draw yet" instead. The height is SC 2.5.8 - the next control sits 6 px below,
          so the spacing exception does not apply.
        */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[9.5px] uppercase tracking-wider ml-auto"
          onClick={() => setExpanded(true)}
        >
          <Maximize2 className="h-3 w-3 mr-1" aria-hidden />
          Timeline
        </Button>
      </div>

      {/*
        THE DEGRADED NOTE COMES FIRST, above the ribbon rather than under it. A reader who stops
        after the picture has to have been told the picture may be wrong - a caveat below the thing
        it qualifies is a caveat most people never reach.
      */}
      {degraded && (
        <p className="text-[10px] leading-snug text-status-red-fg mb-1.5" role="status">
          Coverage read failed.{' '}
          {lastReadAt === null
            ? 'Nothing has been read for this mission - the rotation below is unknown, not empty.'
            // AN ABSOLUTE STAMP, NOT AN AGE. "4 minutes old" is only true at the instant it is
            // rendered and silently rots on a panel nobody re-renders; a UTC clock reading stays
            // true, and it is what every other time on this COP already says.
            : `Showing the last good read, ${utcClock(lastReadAt, timezone)}.`}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="ml-1 underline underline-offset-2 uppercase tracking-wider cursor-pointer"
            >
              Retry
            </button>
          )}
        </p>
      )}

      {/*
        A STOPPED OR SUSPENDED ROTATION FORECASTS NOTHING, and what is drawn under this is history
        plus a plan nobody is going to fly. Said before the picture, for the same reason the
        degraded note is: a caveat below the thing it qualifies is one most readers never reach.
      */}
      {QUIET_STATE[summary.state] && (
        <p className="text-[10px] leading-snug text-status-amber-fg mb-1.5">
          {QUIET_STATE[summary.state]!.note}
        </p>
      )}

      {segments.length > 0 ? (
        <>
          <div
            className="relative flex h-5 overflow-hidden rounded-sm border divider bg-background"
            role="img"
            aria-label={ribbonLabel(coveredFraction, gap, unknownAhead, nowMs, timezone)}
          >
            {segments.map((s) => (
              <div
                key={`${s.startMs}-${s.endMs}-${s.past ? 'p' : 'f'}`}
                style={{
                  // A MINIMUM RENDERED WIDTH FOR A HOLE. A 90-second gap in a four-hour window is
                  // under two pixels and a shorter one rounds away entirely - so the ribbon drew
                  // solid green while NEXT GAP named a hole in words. The picture and the text have
                  // to agree, and the picture is the one an operator reads first.
                  minWidth: s.kind === 'covered' ? undefined : MIN_GAP_PX,
                  width: `${((s.endMs - s.startMs) / (domain!.end - domain!.start)) * 100}%`,
                  ...segmentPaint(s),
                }}
                className="h-full"
              />
            ))}
            {/*
              THE NOW RULE. The window carries up to two hours of lookback, so without it a hole
              that closed ninety minutes ago sits in the same bar as one opening in twenty minutes,
              with nothing on the picture to tell them apart. Red on a coverage ribbon means act.
            */}
            {nowPct !== null && (
              <span
                aria-hidden
                className="absolute inset-y-0 w-px bg-foreground"
                style={{ left: `${nowPct}%` }}
              />
            )}
          </div>
          <div className="flex justify-between text-[9px] font-mono tabular-nums text-muted-foreground mt-0.5">
            {/* `utcClock` already carries the zone, so the middle slot went to a third copy of it.
                It belongs to NOW, which is the one label that tells the operator how much of this
                ribbon is the past. */}
            <span>{utcClock(domain!.start, timezone)}</span>
            <span className="text-foreground">NOW</span>
            <span>{utcClock(domain!.end, timezone)}</span>
          </div>
        </>
      ) : (
        // NOT an empty box. A blank where a ribbon belongs reads as a rendering fault.
        <p className="text-[10px] text-muted-foreground">
          {degraded ? 'No chart has been read.' : 'Nothing to draw yet.'}
        </p>
      )}

      <dl className="mt-2 space-y-0.5">
        <Row label="Next swap" value={
          launchAt
            ? `${utcClock(launchAt, timezone)}${commitState ? ` · ${commitState}` : ''}`
            : rotation === null ? '--' : 'none planned'
        } />
        {/*
          A relief that is NAMED but UNFLYABLE is not the same as no relief, and rendering them the
          same way turns a warning into an empty space. §13's COVERAGE AT RISK: the outgoing
          aircraft must still leave at the published time, with nobody coming to replace it.
        */}
        <Row
          label="Next drone"
          value={reliefCallsign
            ?? (reliefUnavailable ? 'NO RELIEF' : rotation === null ? '--' : 'not named')}
          /* "not named" is amber too, not default foreground. It is the state where the forecast
             names nobody at all - which is exactly the state in which Approve and Rotate now are
             both refused - and rendering it as an ordinary value made the one row that could say
             so look like a blank field rather than a fleet condition. */
          bad={reliefCallsign === null && rotation !== null}
        />
        {/*
          THE SENTENCE GETS THE FULL WIDTH, not the value column. `unavailableReason` is a whole
          sentence from the candidate selector - around 105 characters - and at the 288 px floor
          that column is roughly 30, so putting it there wrapped a five-line monospace paragraph off
          a one-word label and destroyed the four-line scan this block is built around. The mission
          panel solved it the same way: a short chip on the line, the sentence underneath.
        */}
        {reliefCallsign === null && reliefUnavailable !== null && (
          <p className="text-[10px] leading-snug text-status-amber-fg pt-0.5">{reliefUnavailable}</p>
        )}
        {/*
          FOUR ANSWERS, NOT TWO. "none" was printed for "the window is fully manned", for "no chart
          has been read", and for "nothing is forecast" alike - an affirmative all-clear rendered in
          default foreground, sometimes directly under a note saying nothing had been read. That is
          the single misreading this whole block exists to prevent, and this row was where it
          survived. A gap already under way says NOW rather than a start time that has gone past.
        */}
        <Row
          label="Next gap"
          value={timeline === null
            ? '--'
            : gap !== null
              // THE DURATION IS ONLY PRINTED WHEN IT IS KNOWN (#704 review). An observed hole at
              // the NOW rule is drawn as a gap so the operator is told the station is empty, but
              // nothing forecast its end - printing "4h 0m" there would be the chart inventing a
              // length out of the horizon, which is the overclaim #704 was filed about.
              ? `${nowMs !== null && gap.startMs <= nowMs ? 'NOW' : utcClock(gap.startMs, timezone)}${gap.unplanned ? '' : ` · ${formatDuration(Math.round((gap.endMs - gap.startMs) / 1000))}`}`
              : unknownAhead ? 'not forecast' : 'none'}
          bad={gap !== null || unknownAhead}
        />
      </dl>

      {/*
        THE DECISION, AND ONLY WHEN THERE IS ONE.
        `CoverageActionCard` is mounted unmodified - it enforces its own render gate (nothing
        visible and nothing failed means it returns null), carries the written refusal sentence for
        every unavailable control, and keeps the 600 ms arm-then-confirm dwell on ROTATE NOW, the
        one press here that ends with an airframe leaving a pad. Reusing it rather than writing a
        COP version is the point: an operator must not meet two different Approve buttons that
        refuse for different reasons.
      */}
      {decisionOffered && (
        <div className="mt-2">
          <CoverageActionCard
            /* The COP's column is 288-320 px. See the prop's own doc: refusals are unaffected,
               the two standing notes for available controls collapse to `sr-only` behind an
               expander, and arming ROTATE NOW opens them again before the confirm. */
            compact
            proposalVisible={replacement !== null}
            hasProposal={rotation?.current != null}
            rotationFailed={degraded}
            launchUnderWay={launchUnderWay(phase)}
            selfSwapRotation={timeline?.singleAircraftRotation === true}
            automaticLaunch={rotation?.automatic_launch_enabled === true}
            canApprove={canApprove && !degraded}
            canRotateNow={canRotateNow && !degraded}
            busy={controls.busy}
            submitting={controls.submitting === 'detach' ? 'detach' : controls.submitting}
            rotateArmed={rotateArmed}
            armedRotateAt={armed?.control === 'rotate' ? armed.at : null}
            approved={approval?.state === 'approved'}
            approvedNote={approval?.state === 'approved'
              ? `Approved${approval.approved_by_name ? ` by ${approval.approved_by_name}` : ''}`
                + `${approval.approved_at ? ` at ${utcStamp(approval.approved_at, timezone)}` : ''}.`
              : null}
            subject={reliefCallsign != null && launchAt != null
              ? `${reliefCallsign} · launch ${utcStamp(launchAt, timezone)}`
              : launchAt != null ? `launch ${utcStamp(launchAt, timezone)}` : null}
            onApprove={() => {
              const id = rotation?.current?.id;
              if (!id || reliefDroneId === null || launchAt === null) return;
              controls.approve(id, reliefDroneId, launchAt);
            }}
            onArmRotate={() => {
              const actionId = rotation?.current?.id ?? null;
              if (actionId === null) return; // nothing to arm against
              setArmed({ control: 'rotate', actionId, at: Date.now() });
            }}
            onRotateNow={() => {
              // READ OFF THE ARM, NOT FRESH. The whole point of carrying the id is that this
              // commands the proposal the operator actually looked at and armed - taking the
              // current one here would reintroduce the defect from the other end.
              const armedRotation = armed?.control === 'rotate' ? armed : null;
              disarm();
              if (!armedRotation || reliefDroneId === null) return;
              controls.rotateNow(armedRotation.actionId, reliefDroneId);
            }}
            onDisarm={disarm}
            onCancel={() => {
              const id = rotation?.current?.id;
              // Disarm FIRST: cancelling schedules a fresh proposal straight away, and an arm left
              // standing would be live against it the moment the poll brings it back.
              disarm();
              if (id) controls.cancelReplacement(id);
            }}
            uid={uid}
          >
            {/*
              WHAT APPROVING ACTUALLY DOES ON THIS DEPLOYMENT, and it is a different sentence
              depending on the flag - which is why the flag is on the wire at all.

              `CoverageActionCard` gives this child the id its Approve button points at with
              `aria-describedby`, so omitting it (as the first cut did) both dropped the only
              instruction telling an operator whether anything will fly, AND left a dangling
              description on the one control that arms a launch - nothing at all for a screen
              reader. Declarative, never conditional: "this might launch an aircraft or might not"
              is worse than either answer.
            */}
            <p
              id={`${uid}-controls-note`}
              className={rotation?.automatic_launch_enabled === true
                ? 'text-[10px] text-muted-foreground'
                : 'text-[10px] text-status-amber-fg'}
            >
              {rotation?.automatic_launch_enabled === true
                ? 'Automatic launch is ON. Approving arms the replacement; the rotation launches'
                  + ' it at the planned time.'
                : 'Automatic launch is OFF. Approving records the decision; nothing flies until'
                  + ' someone launches the relief.'}
            </p>
          </CoverageActionCard>
        </div>
      )}

      {/*
        THE CAPABILITY'S LIFE, one press further away. Suspend, Resume and Detach are deliberate
        interventions rather than answers to this cycle, and they do not belong in the row that
        also carries "approve this launch" - see `CopCoverageLifecycle`'s own header.
      */}
      {commandable && (
        <CopCoverageLifecycle
          missionId={summary.mission_id}
          suspended={summary.state === 'suspended'}
          stopped={summary.state === 'stopped'}
          selfSwapRotation={timeline?.singleAircraftRotation === true}
          /* Only for the two states that MEAN an aircraft is holding the station. A `gap` can also
             have something airborne - the outgoing aircraft on its way home - but the block cannot
             tell, and naming an aircraft that is not there would be worse than saying nothing. The
             server's refusal remains the authority either way. */
          stationHeldBy={summary.state === 'covered' || summary.state === 'handover'
            ? summary.covered_by_callsign
            : null}
          missionRunning={rotation?.current != null || summary.state === 'covered' || summary.state === 'handover'}
          submitting={controls.submitting}
          busy={controls.busy}
          detachArmed={detachArmed}
          onArmDetach={() => setArmed({ control: 'detach', at: Date.now() })}
          onDisarm={disarm}
          airborneRefusal={controls.airborneRefusal}
          onDismissRefusal={controls.clearAirborneRefusal}
          onSuspend={controls.suspend}
          onResume={controls.resume}
          onDetach={controls.detach}
        />
      )}

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-[96vw] sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-wider text-sm">
              Rotation timeline
            </DialogTitle>
            <DialogDescription>
              Every aircraft in this rotation, across the coverage window. Read-only.
            </DialogDescription>
          </DialogHeader>
          {/*
            THE TWO MEASUREMENTS, BESIDE THE CHART THEY ARE ABOUT.

            They used to sit in the strip's `<dl>` with "Next swap" and "Next gap", which put five
            readings above the first control in a 288 px column and buried the two that actually
            answer "what happens next".

            Moving them is not only decluttering, it fixes what they meant. They are NOT measured
            over the same interval and reading them side by side implied they were: DRAWN WINDOW is
            a percentage of THIS CHART's domain - its lookback plus its horizon - while TARGET is
            set against the whole coverage window, which may be a day long. Under the chart, the
            first number has the thing it is a percentage OF on screen next to it. ("X% of Y%" was
            an earlier attempt at the same problem and read as a percentage of a percentage; a
            target of 0, which a row synthesised from a socket frame before the first fleet poll
            really does carry, rendered as "100.0% of 0%".)
          */}
          {/*
            EACH ONE NAMES ITS OWN INTERVAL, and only rendered when there is a chart to be about.

            Moving them here was supposed to stop them being read as a pair, and stacking them in
            one flex list 24 px apart in identical type put them straight back into one - "92.4
            versus 95" reads as "below target" at 2am whatever the labels say. The intervals are
            what makes them different measurements, so the intervals are on screen.
          */}
          {timeline && domain && (
            <dl className="space-y-1">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Drawn window{' '}
                  <span className="normal-case tracking-normal">
                    ({utcClock(domain.start, timezone)} to {utcClock(domain.end, timezone)}, the
                    chart above)
                  </span>
                </dt>
                <dd className="font-mono text-[11px] tabular-nums shrink-0">
                  {coveredFraction === null
                    ? '--'
                    : `${(coveredFraction * 100).toFixed(1)} % manned`}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Target{' '}
                  <span className="normal-case tracking-normal">
                    (over the whole coverage window)
                  </span>
                </dt>
                <dd className="font-mono text-[11px] tabular-nums shrink-0">
                  {summary.coverage_target_pct > 0 ? `${summary.coverage_target_pct}%` : '--'}
                </dd>
              </div>
            </dl>
          )}
          {timeline && domain ? (
            <>
              <TimelineLegend />
              <TimelineChart view={timeline} domain={domain} timezone={timezone} expanded />
            </>
          ) : (
            <p className="text-xs text-muted-foreground">Nothing to draw yet.</p>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

/**
 * A LABEL THAT NEEDS EXPLAINING IS THE WRONG LABEL.
 *
 * These three rows briefly carried `?` buttons opening a one-line definition each. The operator's
 * answer to that was to remove them and rename the row instead: "Relief" is the jargon, and a help
 * affordance beside it is a second thing to notice in a strip whose whole purpose is a
 * three-second read. So RELIEF became NEXT DRONE, and there is nothing left to explain.
 *
 * The VALUE stays `NO RELIEF`, and that is deliberate rather than an oversight. It is the at-risk
 * chip `MissionCoveragePanel` renders in the same case, and two surfaces naming one fleet condition
 * differently is a defect this feature has already argued against twice.
 */
function Row({ label, value, bad = false }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={cn('font-mono text-[11px] tabular-nums', bad && 'text-status-amber-fg')}>
        {value}
      </dd>
    </div>
  );
}

/**
 * The ribbon's accessible name.
 *
 * A bar chart with no text is nothing at all to a screen reader, and this one carries the reading
 * the block exists for. It says the same two things the pixels do - how much of the window is
 * manned, and when the next hole is - rather than describing the colours.
 */
function ribbonLabel(
  fraction: number | null,
  gap: { startMs: number; endMs: number; unplanned: boolean } | null,
  unknownAhead: boolean,
  nowMs: number | null,
  timezone: string,
): string {
  const share = fraction === null
    ? (unknownAhead ? 'Part of this window is not forecast' : 'Coverage unknown')
    : `${(fraction * 100).toFixed(0)}% of the window is manned`;
  if (!gap) {
    return unknownAhead
      ? `${share}. No gap is forecast, but the rest of the window has no plan.`
      : `${share}. No gap in the window.`;
  }
  const underWay = nowMs !== null && gap.startMs <= nowMs;
  const when = underWay ? 'now' : `at ${utcClock(gap.startMs, timezone)}`;
  // Same rule as the row above: no length is claimed for a hole nothing has forecast the end of.
  if (gap.unplanned) return `${share}. Next gap ${when}; nothing forecasts when it ends.`;
  return `${share}. Next gap ${when}, ${formatDuration(Math.round((gap.endMs - gap.startMs) / 1000))} long.`;
}

